import { computed, inject, provide, ref } from 'vue';
import { storeToRefs } from 'pinia';

import { AccountModal, PackagesSelectionModal } from '@/lazy-load-components';

import { useExperiment } from '@/libraries/experiment';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { useI18n } from '@/helpers/composables/useI18n';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

import type { ComputedRef, Ref } from 'vue';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';
import { AccountModes } from '@/enums/login';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { Package } from '@/@types/graphql-types';

export function useProviderBuyboxFilter(options?: {
	listFilterType?: MaybeRefOrGetter<BuyBoxFilterOptions | null>;
	offerProviders?: MaybeRefOrGetter<string[]>;
	isInternationalOffersActive?: MaybeRefOrGetter<boolean>;
}) {
	const { activeVariantWithControlGroup: providerBuyboxExpVariant, trackEvent } =
		useExperiment('EXP_PROVIDER_BUYBOX');

	const filterStore = useFilterStore();
	const userStore = useUserStore();
	const { selectedProvidersType, currentFilters } = storeToRefs(filterStore);
	const { settingsProvider, seenMyServices } = storeToRefs(useUserStore());
	const { setSeenMyServices } = userStore;

	const { tc } = useI18n();

	const providerFilterOverride = ref<string[] | null>(null);
	const showProviders = ref(false);

	// Inject before providing so child contexts (e.g. filter bar) pass through the ancestor's ref
	const buyboxProviderFilter = inject<Ref<string[] | null> | null>('buyboxProviderFilter', null);
	// In the owner context (e.g. GridBuyBox), inject returns null because a component can't inject
	// its own provide. Fall back to the local ref so all logic works uniformly.
	const effectiveFilter = buyboxProviderFilter ?? providerFilterOverride;
	// Provide effectiveFilter so ProviderFilterMenu always writes to the buybox-level ref
	provide('buyboxProviderFilter', effectiveFilter);
	provide('buyboxProviderMenuOpen', showProviders);
	provide('closeBuyboxProviderMenu', () => {
		showProviders.value = false;
	});
	if (options?.offerProviders) {
		provide('buyboxOfferProviders', options.offerProviders);
	}
	const buyboxOfferProviders = options?.offerProviders
		? computed(() => toValue(options.offerProviders!))
		: inject<ComputedRef<string[]> | null>('buyboxOfferProviders', null);

	const selectedBuyboxProviders = computed<string[] | null>(() => {
		const variant = providerBuyboxExpVariant.value;
		if (!variant || variant === 'control') return null;
		if (variant === 'variant_4') {
			if (options?.listFilterType) {
				return toValue(options.listFilterType) === BuyBoxFilterOptions.MY_SERVICES
					? settingsProvider.value ?? []
					: null;
			}
			// Use effectiveFilter only as an activation flag (null = chip not yet clicked).
			// Read settingsProvider reactively so modal updates are reflected immediately.
			return effectiveFilter.value === null ? null : settingsProvider.value ?? [];
		}
		if (variant === 'variant_3') {
			// null override = first open, all providers selected = no filter applied
			return effectiveFilter.value;
		}
		// variants 1/2: null until user explicitly interacts (override set)
		return effectiveFilter.value;
	});

	const isProviderFilterActive = computed(() => {
		const variant = providerBuyboxExpVariant.value;
		if (!variant || variant === 'control') return false;

		const offerProviders = toValue(options?.offerProviders) ?? [];

		if (variant === 'variant_3') {
			// null override = first open, all providers selected = filter not active
			if (effectiveFilter.value === null) return false;
			const active = effectiveFilter.value;
			if (offerProviders.length > 0) {
				// Grid: compare against providers that actually have offers
				const allProviders = filterStore.selectedProvidersByType();
				const totalCount = allProviders.filter(p => offerProviders.includes(p.shortName)).length;
				const selectedCount = active.filter(p => offerProviders.includes(p)).length;
				return selectedCount < totalCount;
			}
			// List: offerProviders not available; active if override has reduced the buybox provider set
			return active.length < (buyboxOfferProviders?.value?.length ?? 0);
		}

		if (variant === 'variant_2') {
			return effectiveFilter.value !== null;
		}

		if (variant === 'variant_4') {
			if (options?.listFilterType) {
				return toValue(options.listFilterType) === BuyBoxFilterOptions.MY_SERVICES;
			}
			return effectiveFilter.value !== null && effectiveFilter.value.length > 0;
		}

		// variant_1: active when user has explicitly set a non-empty override
		return effectiveFilter.value !== null && effectiveFilter.value.length > 0;
	});

	const buyboxCurrentFilters = computed(() => {
		// variant_3: null override = first open = all buybox providers selected
		if (providerBuyboxExpVariant.value === 'variant_3' && effectiveFilter.value === null) {
			return {
				...filterStore.currentFilters,
				providers: buyboxOfferProviders?.value ?? settingsProvider.value ?? [],
			};
		}
		return {
			...filterStore.currentFilters,
			providers: effectiveFilter.value ?? settingsProvider.value ?? [],
		};
	});

	const myServicesChip = computed(() => {
		if (providerBuyboxExpVariant.value !== 'variant_4') return null;
		return { label: tc('WEBAPP_LISTS_MY_SERVICES'), value: 'my_services' };
	});

	// variant_3: restrict the FilterBarProvider to providers that have actual offers in this buybox
	const providersForVariant3 = computed(() => {
		const allProviders = filterStore.selectedProvidersByType();
		if (!buyboxOfferProviders) return allProviders; // not provided (list view before data loads)
		if (!buyboxOfferProviders.value?.length) return []; // provided but empty = no offers for title
		return allProviders.filter(p => buyboxOfferProviders.value.includes(p.shortName));
	});

	// variant_3 only: count of offer-providers currently active in the buybox filter
	const buyboxActiveProviderCount = computed<number | undefined>(() => {
		if (providerBuyboxExpVariant.value === 'variant_3') {
			// null override = all providers active = same as total
			if (effectiveFilter.value === null) return providersForVariant3.value.length;
			const active = effectiveFilter.value;
			return active.filter(p => buyboxOfferProviders?.value?.includes(p)).length;
		}
		if (providerBuyboxExpVariant.value === 'variant_2') {
			if (effectiveFilter.value === null) {
				return filterStore.selectedProvidersByType('all').length;
			}
			return effectiveFilter.value.length;
		}
		return undefined;
	});

	// variant_3 only: total offer-providers in this buybox — used as totalCount so ProviderFilterButton can show the yellow badge when fewer providers are active
	const buyboxTotalProviderCount = computed<number | undefined>(() => {
		if (providerBuyboxExpVariant.value !== 'variant_3') return undefined;
		return providersForVariant3.value.length;
	});

	const showProviderFilterButton = computed(
		() =>
			!!providerBuyboxExpVariant.value &&
			providerBuyboxExpVariant.value !== 'control' &&
			providerBuyboxExpVariant.value !== 'variant_4' &&
			!toValue(options?.isInternationalOffersActive)
	);

	function deactivateMyServices() {
		if (providerBuyboxExpVariant.value !== 'variant_4') return;
		if (!myServicesActive.value) return;
		effectiveFilter.value = null;
	}

	function handleV3ProviderToggle({ value }: { value: string }) {
		const current = effectiveFilter.value;
		if (current === null) {
			// First interaction from "all shown": activate only this provider
			effectiveFilter.value = [value];
		} else {
			const idx = current.indexOf(value);
			if (idx >= 0) {
				// Deselect: if last one, reset to null (all shown)
				const next = current.filter(p => p !== value);
				effectiveFilter.value = next.length > 0 ? next : null;
			} else {
				effectiveFilter.value = [...current, value];
			}
		}
	}

	async function openMyServicesModal(buyboxType: 'grid' | 'list'): Promise<boolean> {
		let wasReset = false;

		let _buyboxType = 0;
		if (buyboxType === 'grid') _buyboxType = 0;
		if (buyboxType === 'list') _buyboxType = 1;

		trackEvent({
			action: 'click',
			label: 'provider_filter_open',
			value: _buyboxType,
		});

		await ModalHelper.openModal(
			PackagesSelectionModal,
			{
				providerPlace: ProviderFilterPlace.BUYBOX,
				offerProviders: buyboxOfferProviders?.value,
				expVariant: providerBuyboxExpVariant.value,
				onBuyboxProviderToggle: (packageItem: { technicalName: string }) => {
					trackEvent({
						action: 'click',
						label: 'provider_toggle',
						property: packageItem.technicalName,
						value: _buyboxType,
					});
				},
				onSearchbarFocus: () => {
					trackEvent({
						action: 'click',
						label: 'provider_search_focus',
						value: _buyboxType,
					});
				},
				onBuyboxSearched: (term: string) => {
					trackEvent({
						action: 'click',
						label: 'provider_search_focus',
						property: term,
						value: _buyboxType,
					});
				},
				...(providerBuyboxExpVariant.value === 'variant_2'
					? {
							onBuyboxFilterReset: () => {
								wasReset = true;

								trackEvent({
									action: 'click',
									label: 'reset',
									value: _buyboxType,
								});

								trackEvent({
									action: 'click',
									label: 'provider_filter_close',
									value: _buyboxType,
								});
							},
							onBuyboxFilterConfirm: (providers: Package[]) => {
								effectiveFilter.value = providers.map(p => p.shortName);

								trackEvent({
									action: 'click',
									label: 'provider_filter_close',
									property: providers.map(poo => poo.technicalName).join(', '),
									value: _buyboxType,
								});
							},
					  }
					: {}),
				...(providerBuyboxExpVariant.value === 'variant_4'
					? {
							onBuyboxFilterDismiss: () => {
								trackEvent({
									action: 'click',
									label: 'provider_filter_close',
									value: _buyboxType,
								});
							},
							onBuyboxFilterConfirm: (providers: Package[]) => {
								trackEvent({
									action: 'click',
									label: 'provider_filter_close',
									property: providers.map(poo => poo.technicalName).join(', '),
									value: _buyboxType,
								});

								if (!userStore.isLoggedIn) {
									ModalHelper.openModal(
										AccountModal,
										{
											closable: true,
											startScreen: AccountModes.SIGN_UP,
											expVariant: providerBuyboxExpVariant.value,
										},
										{ cssClass: 'account-modal' }
									);

									trackEvent({
										action: 'click',
										label: 'provider_filter_open_signup',
										value: _buyboxType,
									});
								}
							},
					  }
					: {}),
			},
			{ cssClass: 'packages-selection-modal' },
			ProviderFilterPlace.BUYBOX,
			true
		);

		if (wasReset) effectiveFilter.value = null;

		return wasReset;
	}

	async function toggleProviderSelection(buyboxType: 'grid' | 'list') {
		showProviders.value = !showProviders.value;

		if (providerBuyboxExpVariant.value === 'variant_2') {
			await openMyServicesModal(buyboxType);
		}
	}

	const myServicesActive = computed(() => effectiveFilter.value !== null && effectiveFilter.value.length > 0);

	async function ensureMyServicesModalSeen(buyboxType: 'grid' | 'list'): Promise<boolean> {
		if (!seenMyServices.value) {
			const wasReset = await openMyServicesModal(buyboxType);
			setSeenMyServices();
			return wasReset;
		}
		return false;
	}

	async function handleMyServicesChipClick(buyboxType: 'grid' | 'list') {
		if (myServicesActive.value) {
			await openMyServicesModal(buyboxType);
			return;
		}
		const wasReset = await ensureMyServicesModalSeen(buyboxType);
		if (!wasReset && effectiveFilter.value === null) {
			effectiveFilter.value = settingsProvider.value ?? [];
		}
	}

	return {
		selectedBuyboxProviders,
		isProviderFilterActive,
		providerBuyboxExpVariant,
		buyboxCurrentFilters,
		deactivateMyServices,
		ensureMyServicesModalSeen,
		openMyServicesModal,
		buyboxActiveProviderCount,
		buyboxTotalProviderCount,
		handleV3ProviderToggle,
		providersForVariant3,
		showProviders,
		showProviderFilterButton,
		toggleProviderSelection,
		myServicesChip,
		myServicesActive,
		handleMyServicesChipClick,
	};
}
